import { Message } from '../typings/message-types';
import { getDate, timeString } from '../../../../shared/utils/get-time-from-timestamp';

const messageDateCache: {
  [chatId: string]: {
    groups: Record<string, Set<number>>;
    length: number;
    lastUpdateTime: number;
  };
} = {};

// Clear existing cache on module reload - this is needed to fix stale caches
Object.keys(messageDateCache).forEach((key) => delete messageDateCache[key]);

// Get the date part in format DD/MM/YYYY for a timestamp
const getDateForMessageGroup = (timestamp: number) => {
  const formattedDate = getDate(timestamp);
  return formattedDate;
};

// Calculate today and yesterday dates whenever needed (instead of once at module load)
const getTodayDate = () => {
  const today = getDateForMessageGroup(Date.now());
  return today;
};

const getYesterdayDate = () => {
  const yesterday = getDateForMessageGroup(Date.now() - 86400000);
  return yesterday;
};

// Debug function to check message dates
const logMessageDates = (messages: Message[]) => {
  if (messages.length === 0) return;

  // Log a few sample messages
  const samples = [0, Math.floor(messages.length / 2), messages.length - 1];
  samples.forEach((idx) => {
    if (messages[idx] && messages[idx].messageTime) {
      const timestamp = messages[idx].messageTime;
      const date = getDateForMessageGroup(timestamp);
      const today = getTodayDate();
      const yesterday = getYesterdayDate();
    }
  });
};

// Clear cache for a chatId when data changes significantly
export const clearMessageDateCache = (chatId: string) => {
  if (messageDateCache[chatId]) {
    delete messageDateCache[chatId];
  }
};

// Clear all caches when day changes
export const clearAllDateCaches = () => {
  Object.keys(messageDateCache).forEach((chatId) => {
    delete messageDateCache[chatId];
  });
};

const getGroupedMessages = (data: Message[]) => {
  // Log some sample message dates for debugging
  logMessageDates(data);

  // Get fresh today/yesterday dates
  const todayDate = getTodayDate();
  const yesterdayDate = getYesterdayDate();

  // Create object to hold messages grouped by date
  const groups: Record<string, Set<number>> = {};
  const dateToLabel: Record<string, string> = {}; // Keep track of which raw dates became Today/Yesterday

  // To ensure correct date order
  const dateOrder: string[] = [];
  const dateSet = new Set<string>();

  // Group messages by date
  data.forEach((message, index) => {
    if (!message.messageTime) {
      return;
    }

    // Get the date string for this message
    const rawDateKey = getDateForMessageGroup(message.messageTime);
    let dateKey = rawDateKey;

    // Replace with "Today" or "Yesterday" if applicable
    if (rawDateKey === todayDate) {
      dateKey = timeString.TODAY;
      dateToLabel[rawDateKey] = timeString.TODAY;
    } else if (rawDateKey === yesterdayDate) {
      dateKey = timeString.YESTERDAY;
      dateToLabel[rawDateKey] = timeString.YESTERDAY;
    }

    // Debug individual message grouping
    if (index % 50 === 0 || index === data.length - 1) {
    }

    // Initialize this date group if it doesn't exist
    if (!groups[dateKey]) {
      groups[dateKey] = new Set<number>();

      // Track the order of dates
      if (!dateSet.has(dateKey)) {
        dateSet.add(dateKey);
        dateOrder.push(dateKey);
      }
    }

    // Add this message's index to the date group
    groups[dateKey].add(index);
  });

  // Ensure the groups are returned in chronological order
  const orderedGroups: Record<string, Set<number>> = {};
  dateOrder.sort((a, b) => {
    // Keep TODAY and YESTERDAY at the end
    if (a === timeString.TODAY) return 1;
    if (b === timeString.TODAY) return -1;
    if (a === timeString.YESTERDAY) return 1;
    if (b === timeString.YESTERDAY) return -1;

    // Sort other dates chronologically (assuming DD/MM/YYYY format)
    const [dayA, monthA, yearA] = a.split('/').map(Number);
    const [dayB, monthB, yearB] = b.split('/').map(Number);

    if (yearA !== yearB) return yearA - yearB;
    if (monthA !== monthB) return monthA - monthB;
    return dayA - dayB;
  });

  // Build the ordered result
  dateOrder.forEach((date) => {
    orderedGroups[date] = groups[date];
  });

  // Log summary of groups

  return orderedGroups;
};

const getMessageDateGroups = (chatId: string, data: Message[]) => {
  // If no data, return empty results
  if (!data || data.length === 0) {
    return { groupCounts: [], groupKeys: [] };
  }

  // Check if cache is expired (day has changed since last update)
  const now = new Date();
  const today = now.toDateString();
  let shouldRecalculate = false;

  if (messageDateCache[chatId]) {
    const lastUpdate = new Date(messageDateCache[chatId].lastUpdateTime);
    const lastUpdateDay = lastUpdate.toDateString();

    // Force recalculation if the day has changed (Today/Yesterday labels would be wrong)
    if (lastUpdateDay !== today) {
      shouldRecalculate = true;
    }
  }

  // Normal check for cache or significant data change
  const cachedLength = messageDateCache[chatId]?.length || 0;
  shouldRecalculate = shouldRecalculate || !messageDateCache[chatId] || Math.abs(data.length - cachedLength) > 10;

  if (shouldRecalculate) {
    const groupedMessages = getGroupedMessages(data);
    messageDateCache[chatId] = {
      groups: groupedMessages,
      length: data.length,
      lastUpdateTime: Date.now(),
    };
  } else if (data.length > cachedLength) {
    // Handle incremental updates (new messages)
    const newData = data.slice(cachedLength);
    const newGroupedMessages = getGroupedMessages(newData);

    if (Object.keys(newGroupedMessages).length > 0) {
      Object.keys(newGroupedMessages).forEach((key) => {
        if (!messageDateCache[chatId].groups[key]) {
          messageDateCache[chatId].groups[key] = new Set<number>();
        }
        newGroupedMessages[key].forEach((i) => messageDateCache[chatId].groups[key].add(i + cachedLength));
      });
    }

    messageDateCache[chatId].length = data.length;
    messageDateCache[chatId].lastUpdateTime = Date.now();
  }

  const groupEntries = Object.entries(messageDateCache[chatId].groups);

  // Sort the group entries chronologically
  groupEntries.sort((a, b) => {
    const keyA = a[0];
    const keyB = b[0];

    // TODAY and YESTERDAY come last (most recent)
    if (keyA === timeString.TODAY) return 1;
    if (keyB === timeString.TODAY) return -1;
    if (keyA === timeString.YESTERDAY) return 1;
    if (keyB === timeString.YESTERDAY) return -1;

    // Sort other dates chronologically
    const [dayA, monthA, yearA] = keyA.split('/').map(Number);
    const [dayB, monthB, yearB] = keyB.split('/').map(Number);

    if (yearA !== yearB) return yearA - yearB;
    if (monthA !== monthB) return monthA - monthB;
    return dayA - dayB;
  });

  // Extract sorted keys and counts
  const groupKeys = groupEntries.map(([key]) => key);
  const groupCounts = groupEntries.map(([_, indices]) => indices.size);

  return { groupCounts, groupKeys };
};

export default getMessageDateGroups;
